import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AppDrawer, DRAWER_WIDTH } from './AppDrawer';
import { useTransferSidebar } from '../../../context/TransferSidebarContext';
import { useTransferManagerContext } from '../../../context/TransferManagerContext';
import { TransferQueuePanel } from './TransferQueuePanel';
import { TransferHistoryPanel } from './TransferHistoryPanel';
import ButtonClose from '../_misc/ButtonClose';

const SIDEBAR_TRANSITION = '420ms cubic-bezier(0.22, 1, 0.36, 1)';

const TransferSidebarContent: React.FC<{
  isSmallScreen: boolean;
  onClose: () => void;
}> = ({ isSmallScreen, onClose }) => (
  <Box
    component="section"
    aria-label="Project transfers"
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      overflow: 'hidden',
    }}
  >
    {isSmallScreen && (
      <ButtonClose
        onClick={onClose}
        ariaLabel="Close transfers"
        tooltipTitle="Close transfers"
        dataTestId="transfer-sidebar-mobile-close"
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1,
          bgcolor: 'background.paper',
        }}
      />
    )}

    {/* Queue — scrollable, takes remaining space */}
    <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
      <TransferQueuePanel />
    </Box>

    {/* History — pinned at bottom */}
    <TransferHistoryPanel />
  </Box>
);

export const TransferSidebar: React.FC = () => {
  const { open, setOpen, toggle } = useTransferSidebar();
  const { activeJob } = useTransferManagerContext();
  const hasActiveTransfer = !!activeJob;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {/* Folder tab — always visible on desktop; on mobile visible while collapsed */}
      {(!isSmallScreen || !open) && (
        <Box
          data-testid="transfer-sidebar-tab"
          onClick={toggle}
          sx={{
            position: 'fixed',
            right: open ? `${DRAWER_WIDTH}px` : isSmallScreen ? '0px' : '0px',
            // Keep a stable top offset across breakpoints to avoid jump on resize.
            top: 80,
            transform: 'none',
            width: 28,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'studio.surfaces.panel',
            border: '1px solid',
            borderRight: 'none',
            borderColor: 'studio.borders.control',
            borderRadius: '12px 0 0 12px',
            boxShadow: (theme) => theme.palette.studio.shadows.drawer,
            transition: `right ${SIDEBAR_TRANSITION}, top ${SIDEBAR_TRANSITION}`,
            // Keep tab below dialog stacking context (1300)
            zIndex: 700,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              userSelect: 'none',
              letterSpacing: '0.08em',
              color: hasActiveTransfer ? 'success.main' : 'text.secondary',
              fontWeight: 500,
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
            }}
          >
            Transfers
          </Typography>
        </Box>
      )}

      <AppDrawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <TransferSidebarContent
          isSmallScreen={isSmallScreen}
          onClose={() => setOpen(false)}
        />
      </AppDrawer>
    </>
  );
};
